/**
 * LabPage – "SurrealEstateLab" Father Landing Page
 *
 * Displays all three pillars side-by-side with login/register built in.
 * Uses anthracite as the guiding colour scheme.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  login as loginService,
  register as registerService,
  forgotPassword as forgotPasswordService,
  validatePassword,
} from '../services/authService';

/* ─── constants ─── */
const API_BASE = import.meta.env.VITE_API_URL || '';

interface HeroImageSettings {
  enabled: boolean;
  filename: string | null;
  opacity: number;
  blend_mode: string;
  blur: number;
}

/* ─── pillar data ─── */
const PILLARS = [
  {
    key: 'search',
    name: 'Search',
    color: '#6366f1',
    glow: 'rgba(99,102,241,0.25)',
    route: '/search',
    icon: (
      <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    headline: 'AI-Immobiliensuche',
    description:
      'Erzählen Sie unserer AI von Ihrem Leben – wir finden die passende Immobilie in der Schweiz. Kein Formular, kein Filter-Chaos.',
    cta: 'Suche starten',
  },
  {
    key: 'rate',
    name: 'Rate',
    color: '#00CED1',
    glow: 'rgba(0,206,209,0.25)',
    route: '/rate',
    icon: (
      <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    headline: 'Immobilienbewertung',
    description:
      'Laden Sie ein Immobilieninserat hoch und erhalten Sie in Sekunden eine detaillierte AI-Marktanalyse mit realistischer Preisspanne.',
    cta: 'Bewertung starten',
  },
  {
    key: 'newbuild',
    name: 'Newbuild',
    color: '#C75B4A',
    glow: 'rgba(199,91,74,0.25)',
    route: '/newbuild',
    icon: (
      <svg viewBox="0 0 24 26" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" />
      </svg>
    ),
    headline: 'Neubau-Machbarkeit',
    description:
      'Prüfen Sie Ihr Neubauprojekt: AI-Chat für Ihre Wünsche, Dokument-Upload und automatische Machbarkeitsprüfung.',
    cta: 'Neubau planen',
  },
];

/* ═══════════════════════════════════════════
   Component
   ═══════════════════════════════════════════ */

export default function LabPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, login } = useAuth();

  /* ── auth form collapsed/expanded state ── */
  const [showAuthForm, setShowAuthForm] = useState(false);

  /* ── scroll to #lab-auth when arriving via hash & auto-expand ── */
  useEffect(() => {
    if (location.hash === '#lab-auth') {
      setShowAuthForm(true);
      setTimeout(() => {
        document.getElementById('lab-auth')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [location.hash]);

  /* ── auth form state ── */
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotPwEmail, setForgotPwEmail] = useState('');
  const [forgotPwLoading, setForgotPwLoading] = useState(false);
  const [forgotPwMessage, setForgotPwMessage] = useState('');

  /* ── contact modal ── */
  const [showContact, setShowContact] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactResult, setContactResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const contactTimestamp = useRef(Date.now());

  // Pre-fill email when logged in
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setContactEmail(user.email);
    }
    if (isAuthenticated && user?.name) {
      setContactName(user.name);
    }
  }, [isAuthenticated, user]);

  // Reset timestamp when modal opens
  useEffect(() => {
    if (showContact) {
      contactTimestamp.current = Date.now();
      setContactResult(null);
    }
  }, [showContact]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactLoading(true);
    setContactResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          subject: contactSubject,
          message: contactMessage,
          website: '',           // honeypot – always empty
          _t: contactTimestamp.current, // time-based check
        }),
      });
      const data = await res.json();
      if (data.success) {
        setContactResult({ ok: true, msg: data.message || 'Nachricht gesendet!' });
        setContactSubject('');
        setContactMessage('');
      } else {
        setContactResult({ ok: false, msg: data.error?.message || 'Fehler beim Senden.' });
      }
    } catch {
      setContactResult({ ok: false, msg: 'Netzwerkfehler. Bitte versuchen Sie es später erneut.' });
    } finally {
      setContactLoading(false);
    }
  };

  /* ── background image ── */
  const [heroImage, setHeroImage] = useState<HeroImageSettings | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/lab-page-image-settings`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setHeroImage(data.data);
        }
      })
      .catch(() => {});
  }, []);

  /* ── auth handlers ── */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const { user: u } = await loginService({ email, password });
      login(u);
    } catch (err: any) {
      setAuthError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (name.length < 2) { setAuthError('Name muss mindestens 2 Zeichen lang sein'); return; }
    const pwErr = validatePassword(password);
    if (pwErr) { setAuthError(pwErr); return; }
    if (password !== confirmPassword) { setAuthError('Passwörter stimmen nicht überein'); return; }
    setAuthLoading(true);
    try {
      await registerService({ name, email, password });
      setRegisterSuccess(true);
    } catch (err: any) {
      setAuthError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setForgotPwMessage('');
    if (!forgotPwEmail) { setAuthError('Bitte geben Sie Ihre E-Mail-Adresse ein'); return; }
    setForgotPwLoading(true);
    try {
      const result = await forgotPasswordService(forgotPwEmail);
      setForgotPwMessage(result.message);
    } catch (err: any) {
      setAuthError(err.message || 'Fehler beim Senden der E-Mail');
    } finally {
      setForgotPwLoading(false);
    }
  };

  /* ═══ Render ═══ */
  return (
    <div className="lab-page">
      {/* ── background layers ── */}
      <div className="lab-orb lab-orb--1" />
      <div className="lab-orb lab-orb--2" />
      <div className="lab-orb lab-orb--3" />

      {heroImage && heroImage.enabled && heroImage.filename && (
        <div
          className="lab-bg-image"
          style={{
            backgroundImage: `url(/uploads/${heroImage.filename})`,
            opacity: heroImage.opacity / 100,
            mixBlendMode: heroImage.blend_mode as any,
            filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined,
          }}
        />
      )}

      {/* ── Hero section ── */}
      <section className="lab-hero">
        <h1 className="lab-title">
          SurrealEstate<span className="lab-title-accent">Lab</span>
        </h1>
        <p className="lab-subtitle">
          Ihre zentrale Plattform für AI-gestützte Immobilien- und Architekturlösungen in der Schweiz.
          Suchen, bewerten und planen – alles an einem Ort.
        </p>
      </section>

      {/* ── User avatar (when logged in) ── */}
      {isAuthenticated && user && (
        <div className="lab-user-badge">
          <Link to="/lab/profile" className="lab-user-avatar">
            <span>{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
          </Link>
          <span className="lab-user-name">{user.name}</span>
        </div>
      )}

      {/* ── Pillar cards ── */}
      <section className="lab-pillars">
        {PILLARS.map((p) => (
          <div
            key={p.key}
            className="lab-card"
            style={{ '--pillar-color': p.color, '--pillar-glow': p.glow } as React.CSSProperties}
          >
            <div className="lab-card-icon">{p.icon}</div>
            <h2 className="lab-card-name">
              SurrealEstate<span style={{ color: p.color }}>{p.name}</span>
            </h2>
            <h3 className="lab-card-headline">{p.headline}</h3>
            <p className="lab-card-desc">{p.description}</p>
            <button
              className="lab-card-cta"
              onClick={() => navigate(p.route)}
            >
              {p.cta}
            </button>
          </div>
        ))}
      </section>

      {/* ── Login / Register section ── */}
      <section className="lab-auth-section" id="lab-auth">
        {isAuthenticated && user ? null : registerSuccess ? (
          <div className="lab-auth-card lab-auth-card--success">
            <div className="lab-auth-success-icon">&#10003;</div>
            <h2>Registrierung erfolgreich!</h2>
            <p>
              Wir haben Ihnen eine Bestätigungs-E-Mail an <strong>{email}</strong> gesendet.
              Bitte überprüfen Sie Ihr Postfach und klicken Sie auf den Bestätigungslink.
            </p>
            <button className="lab-auth-btn" onClick={() => { setRegisterSuccess(false); setAuthMode('login'); }}>
              Jetzt anmelden
            </button>
          </div>
        ) : !showAuthForm ? (
          /* ── Collapsed: slim toggle buttons ── */
          <div className="lab-auth-toggle">
            <button
              className="lab-auth-toggle-btn"
              onClick={() => { setAuthMode('login'); setShowAuthForm(true); }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Anmelden
            </button>
            <span className="lab-auth-toggle-divider" />
            <button
              className="lab-auth-toggle-btn"
              onClick={() => { setAuthMode('register'); setShowAuthForm(true); }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              Registrieren
            </button>
          </div>
        ) : (
          /* ── Expanded: full auth form ── */
          <div className="lab-auth-card lab-auth-card--animated">
            <div className="lab-auth-tabs">
              <button
                className={`lab-auth-tab ${authMode === 'login' ? 'lab-auth-tab--active' : ''}`}
                onClick={() => { setAuthMode('login'); setAuthError(''); }}
              >
                Anmelden
              </button>
              <button
                className={`lab-auth-tab ${authMode === 'register' ? 'lab-auth-tab--active' : ''}`}
                onClick={() => { setAuthMode('register'); setAuthError(''); }}
              >
                Registrieren
              </button>
              <button
                className="lab-auth-collapse-btn"
                onClick={() => setShowAuthForm(false)}
                title="Schliessen"
              >
                ✕
              </button>
            </div>

            {authError && (
              <div className="lab-auth-error">
                <span>&#9888;</span>
                <p>{authError}</p>
              </div>
            )}

            {authMode === 'login' && !showForgotPw ? (
              <>
                <form className="lab-auth-form" onSubmit={handleLogin}>
                  <div className="lab-auth-field">
                    <label htmlFor="lab-email">E-Mail-Adresse</label>
                    <input
                      id="lab-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.ch"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="lab-auth-field">
                    <label htmlFor="lab-password">Passwort</label>
                    <input
                      id="lab-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Ihr Passwort"
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <button type="submit" className="lab-auth-btn" disabled={authLoading}>
                    {authLoading ? 'Anmeldung...' : 'Anmelden'}
                  </button>
                </form>
                <p className="lab-auth-forgot">
                  <button type="button" className="lab-auth-forgot-link" onClick={() => { setShowForgotPw(true); setAuthError(''); setForgotPwMessage(''); setForgotPwEmail(email); }}>
                    Passwort vergessen?
                  </button>
                </p>
              </>
            ) : authMode === 'login' && showForgotPw ? (
              <form className="lab-auth-form" onSubmit={handleForgotPassword}>
                {forgotPwMessage ? (
                  <div className="lab-auth-success-msg">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <p>{forgotPwMessage}</p>
                  </div>
                ) : (
                  <>
                    <p className="lab-auth-forgot-desc">
                      Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
                    </p>
                    <div className="lab-auth-field">
                      <label htmlFor="lab-forgot-email">E-Mail-Adresse</label>
                      <input
                        id="lab-forgot-email"
                        type="email"
                        value={forgotPwEmail}
                        onChange={(e) => setForgotPwEmail(e.target.value)}
                        placeholder="ihre@email.ch"
                        required
                        autoComplete="email"
                      />
                    </div>
                    <button type="submit" className="lab-auth-btn" disabled={forgotPwLoading}>
                      {forgotPwLoading ? 'Wird gesendet...' : 'Link senden'}
                    </button>
                  </>
                )}
                <p className="lab-auth-forgot">
                  <button type="button" className="lab-auth-forgot-link" onClick={() => { setShowForgotPw(false); setAuthError(''); setForgotPwMessage(''); }}>
                    ← Zurück zur Anmeldung
                  </button>
                </p>
              </form>
            ) : (
              <form className="lab-auth-form" onSubmit={handleRegister}>
                <div className="lab-auth-field">
                  <label htmlFor="lab-name">Name</label>
                  <input
                    id="lab-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ihr vollständiger Name"
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="lab-auth-field">
                  <label htmlFor="lab-reg-email">E-Mail-Adresse</label>
                  <input
                    id="lab-reg-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.ch"
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="lab-auth-field">
                  <label htmlFor="lab-reg-password">Passwort</label>
                  <input
                    id="lab-reg-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 Zeichen, 1 Grossbuchstabe, 1 Sonderzeichen"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="lab-auth-field">
                  <label htmlFor="lab-reg-confirm">Passwort bestätigen</label>
                  <input
                    id="lab-reg-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Passwort wiederholen"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <button type="submit" className="lab-auth-btn" disabled={authLoading}>
                  {authLoading ? 'Registrierung...' : 'Registrieren'}
                </button>
              </form>
            )}
          </div>
        )}
      </section>

      {/* ── Footer ── */}
      <footer className="lab-footer">
        <div className="lab-footer-links">
          <span className="lab-footer-copy">&copy; {new Date().getFullYear()} SurrealEstate<span className="lab-footer-accent">Lab</span></span>
          <span className="lab-footer-sep">&middot;</span>
          <Link to="/datenschutz" className="lab-footer-link">Datenschutzerklärung</Link>
          <span className="lab-footer-sep">&middot;</span>
          <button type="button" className="lab-footer-link lab-footer-link--btn" onClick={() => setShowContact(true)}>Kontakt</button>
        </div>
      </footer>

      {/* ── Contact Modal ── */}
      {showContact && (
        <div className="lab-contact-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowContact(false); }}>
          <div className="lab-contact-modal">
            <div className="lab-contact-header">
              <h2>Kontakt</h2>
              <button className="lab-contact-close" onClick={() => setShowContact(false)} title="Schliessen">&times;</button>
            </div>

            {contactResult?.ok ? (
              <div className="lab-contact-success">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                <p>{contactResult.msg}</p>
                <button className="lab-auth-btn" onClick={() => setShowContact(false)}>Schliessen</button>
              </div>
            ) : (
              <form className="lab-contact-form" onSubmit={handleContactSubmit}>
                {/* Honeypot – hidden from users, bots fill this */}
                <input type="text" name="website" style={{ position: 'absolute', left: '-9999px', tabIndex: -1 } as any} autoComplete="off" tabIndex={-1} />

                {contactResult && !contactResult.ok && (
                  <div className="lab-auth-error">
                    <span>&#9888;</span>
                    <p>{contactResult.msg}</p>
                  </div>
                )}

                <div className="lab-auth-field">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Ihr Name"
                    required
                    minLength={2}
                    maxLength={100}
                  />
                </div>

                <div className="lab-auth-field">
                  <label htmlFor="contact-email">E-Mail-Adresse</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="ihre@email.ch"
                    required
                    readOnly={isAuthenticated && !!user?.email}
                    className={isAuthenticated && user?.email ? 'lab-contact-email--readonly' : ''}
                  />
                </div>

                <div className="lab-auth-field">
                  <label htmlFor="contact-subject">Betreff</label>
                  <input
                    id="contact-subject"
                    type="text"
                    value={contactSubject}
                    onChange={(e) => setContactSubject(e.target.value)}
                    placeholder="Worum geht es?"
                    required
                    minLength={2}
                    maxLength={200}
                  />
                </div>

                <div className="lab-auth-field">
                  <label htmlFor="contact-message">Nachricht</label>
                  <textarea
                    id="contact-message"
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Ihre Nachricht..."
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={5}
                  />
                </div>

                <button type="submit" className="lab-auth-btn" disabled={contactLoading}>
                  {contactLoading ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
